import { Button, Flex, Modal, Stack, Text, Box, Divider } from "@mantine/core";
import { useState, useEffect } from "react";
import { showNotification } from "@mantine/notifications";
import { useAuth0 } from "@auth0/auth0-react";
import { IconCheck, IconX } from "@tabler/icons";
import ReactTimeAgo from "react-time-ago";
import useEvidenceActions from "../../../../../hooks/useEvidenceActions";
import { getLatestEvidenceLog, ideaValidation, updateValidation } from "../../../../../Api";
import AssignedIdeaInfo from "../actions/MatchActions/AssignedIdeaInfo";
import { useParams } from "react-router-dom";

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleString();
};

interface EvidenceLog {
  userName: string;
  timestamp: string;
}

const AiAssignmentVerificationModal = ({
  verifyOpened,
  setVerifyOpened,
  navToPerspective,
  reassignedRequest,
  handleNextEvidenceOrClose,
  refetchEvidences,
  chosenEvidence,
  validation,
  setideaValidationData,
  setMaturity,
  handlebannerText,
  setLoading,
  setValidationCount,
  setIsValidationDone,
  multiSelect = false,
  selectedEvidenceIds,
  setSelectedEvidenceIds,
  idea
}: {
  verifyOpened: boolean;
  setVerifyOpened: React.Dispatch<React.SetStateAction<boolean>>;
  navToPerspective: any
  reassignedRequest: any
  chosenEvidence: any
  handleNextEvidenceOrClose: () => void,
  refetchEvidences: () => void,
  validation?: boolean,
  setideaValidationData?: any,
  setMaturity?: any,
  handlebannerText?: any,
  setLoading?: (loading: boolean) => void,
  setValidationCount?: any,
  setIsValidationDone:any,
  multiSelect?: boolean,
  selectedEvidenceIds?: any,
  setSelectedEvidenceIds?: any,
  idea?: any
}) => {
  const auth0 = useAuth0();
  const { id: ideaId } = useParams<{ id: string }>();

  const { validate, isLoading, validateMultiple } = useEvidenceActions({ evidenceId: chosenEvidence?._id });

  const assignedIdea = (chosenEvidence?.idea && Object.keys(chosenEvidence?.idea).length > 0)
    ? chosenEvidence?.idea
    : null;

  const [evidenceLog, setEvidenceLog] = useState<EvidenceLog | null>(null);
  const [assignedEvidences, setAssignedEvidences] = useState<any[]>([]);

  useEffect(() => {
    const fetchEvidenceLog = async () => {
      if (chosenEvidence?._id) {
        try {
          const data = await getLatestEvidenceLog(auth0, chosenEvidence?._id);
          setEvidenceLog(data);
        } catch (error) {
          console.error('Error fetching evidence log:', error);
        }
      }
    };
    fetchEvidenceLog();
  }, [chosenEvidence?._id]);

  // For multi-select, identify which evidences are already assigned
  useEffect(() => {
    if (multiSelect && selectedEvidenceIds) {
      const assigned = selectedEvidenceIds.filter((evidence: any) => 
        evidence?.ideaId != null && evidence?.ideaId != idea._id
      );
      setAssignedEvidences(assigned);
    }
  }, [multiSelect, selectedEvidenceIds, idea._id]);

  const handleReassignRequestWithTextItemFromMatch = (evidenceId: string, matchId: string) => {
    if (multiSelect) {
      // Filter out evidences that are already assigned to current idea
      const filteredEvidences = selectedEvidenceIds.filter((evidence: any) => 
        evidence?.ideaId != idea._id
      );
      
      const evidences: any = filteredEvidences.map((evidence: any) => ({
        evidenceId: evidence._id,
        ...(evidence.match ? { matchId: evidence.match._id, sourceOfAssignment: "suggestion" } : {})
      }));

      if (setLoading) {
        setLoading(true);
      }

      validateMultiple({ ideaId: idea?._id, oldIdeaId: ideaId, evidences })
        .then(() => {
          if (setLoading) {
            setLoading(false);
          }
          showNotification({
            title: "Evidences have been assigned successfully",
            color: "teal",
            message: "",
            icon: <IconCheck size="1.1rem" />,
            autoClose: 1200,
          });
          setVerifyOpened(false);
          refetchEvidences?.();
          /* navToPerspective(); */
          setSelectedEvidenceIds([]);
        })
        .catch((e) => {
          console.error(e);
          if (setLoading) {
            setLoading(false);
          }
          showNotification({
            title: "Error assigning evidences",
            color: "red",
            message: "Please try again...",
            icon: <IconX size="1.1rem" />,
            autoClose: 1200,
          });
          setSelectedEvidenceIds([]);
        });
    } else {
      // Single select logic
      if (validation) {
        setLoading?.(true);
      }
      validate({
        ideaId: idea?._id,
        matchId,
        sourceOfAssignment: "suggestion"
      })
        .then(() => {
          showNotification({
            title: "Evidence has been re-assigned successfully",
            color: "teal",
            message: "",
            icon: <IconCheck size="1.1rem" />,
            autoClose: 1200
          });
          setVerifyOpened(false);
          handleNextEvidenceOrClose?.();
          refetchEvidences() 
          if (validation) {
            ideaValidation(auth0, idea._id)
              .then((res) => {
                setideaValidationData(res.data);
                setMaturity(res.data.maturityLevel);
                setValidationCount((prevCount: number) => {
                  const newCount = prevCount + 1;
                  handlebannerText(newCount);
                  const idea_id = idea._id;
                  updateValidation(
                    auth0,
                    { id: idea_id, verifyState: 'continue', validationCount: newCount }
                  )
                  if (newCount >= 5) {
                    setIsValidationDone(true);
                  }
                  return newCount;
                });
                setLoading?.(false);
              })
              .catch((e) => console.log(e));
          }
        })
        .catch((e) => {
          console.log(e);
          showNotification({
            title: "Error re-assigning request",
            color: "red",
            message: "please try again...",
            icon: <IconX size="1.1rem" />,
            autoClose: 1200
          });
        });
    }
  }

  const renderModalContent = () => {
    if (multiSelect) {
      return (
        <Stack mx="md" mb={5}>
          <Text fz={16} fw={500} mb={10}>
            Evidence Assignment Confirmation
          </Text>
          
          <Text fz={14} mb={10}>
            {assignedEvidences.length === 1 
              ? "1 evidence is already assigned to another idea:"
              : `${assignedEvidences.length} evidences are already assigned to other ideas:`
            }
          </Text>

          {assignedEvidences.length > 0 && (
            <Box mb={15}>
              {assignedEvidences.map((evidence: any, index: number) => (
                <Box key={evidence._id} mb={8}>
                  <Text fz={12} c="dimmed" lineClamp={1}>
                    Evidence {index + 1}: {evidence.title || evidence.description || 'Untitled Evidence'}
                  </Text>
                  <AssignedIdeaInfo assignedIdea={evidence.idea} />
                  {index < Math.min(assignedEvidences.length - 1, 2) && <Divider my={5} />}
                </Box>
              ))}
            </Box>
          )}

          <Text fz={14} mb={15}>
            Would you like to reassign {assignedEvidences.length === 1 ? 'this evidence' : 'these evidences'} to the current idea?
          </Text>

          <Flex justify="space-around" style={{ width: "100%" }} mt={13}>
            <Button
              mx="md"
              onClick={() => setVerifyOpened(false)}
              styles={(theme) => ({
                root: {
                  height: "32px",
                  backgroundColor: "#DB2525",
                  fontWeight: 400,
                  "&:hover": {
                    backgroundColor: theme.fn.darken("#DB2525", 0.05),
                  },
                },
                leftIcon: {
                  marginRight: 15,
                },
              })}
            >
              Cancel
            </Button>

            <Button
              mx="md"
              styles={(theme) => ({
                root: {
                  height: "32px",
                  backgroundColor: "#5C5CEB",
                  fontWeight: 400,
                  "&:hover": {
                    backgroundColor: theme.fn.darken("#5C5CEB", 0.05),
                  },
                },
                leftIcon: {
                  marginRight: 15,
                },
              })}
              onClick={() => handleReassignRequestWithTextItemFromMatch(chosenEvidence?._id, chosenEvidence?.match?._id)}
              loading={isLoading.validateMultiple}
            >
              Reassign All
            </Button>
          </Flex>
        </Stack>
      );
    } else {
      // Single select UI
      return (
        <Stack mx="md" mb={5}>
          <Stack mb={5}>
            <Text fz={14} mr={5} lineClamp={2}>
              {evidenceLog?.userName ?
                `The Evidence has been assigned to another Idea by "${evidenceLog.userName}" :` :
                'The Evidence has already been assigned to another Idea:'
              }
            </Text>
            {evidenceLog?.timestamp &&
              <Text fz={12} c="dimmed" mb={-10} lineClamp={1}>
                <ReactTimeAgo date={new Date(evidenceLog.timestamp)} title={formatDate(evidenceLog.timestamp)} />
              </Text>
            }
            <Flex justify="space-between" mb={4}>
              <AssignedIdeaInfo assignedIdea={assignedIdea} />
            </Flex>
          </Stack>
          <Text fz={14}>
            Would you like to reassign this Evidence to the current Idea?
          </Text>

          <Flex justify="space-around" style={{ width: "100%" }} mt={13}>
            <Button
              mx="md"
              onClick={() => setVerifyOpened(false)}
              styles={(theme) => ({
                root: {
                  height: "32px",
                  backgroundColor: "#DB2525",
                  fontWeight: 400,
                  "&:hover": {
                    backgroundColor: theme.fn.darken("#DB2525", 0.05),
                  },
                },
                leftIcon: {
                  marginRight: 15,
                },
              })}
            >
              Cancel
            </Button>

            <Button
              mx="md"
              styles={(theme) => ({
                root: {
                  height: "32px",
                  backgroundColor: "#5C5CEB",
                  fontWeight: 400,
                  "&:hover": {
                    backgroundColor: theme.fn.darken("#5C5CEB", 0.05),
                  },
                },
                leftIcon: {
                  marginRight: 15,
                },
              })}
              onClick={() => handleReassignRequestWithTextItemFromMatch(chosenEvidence?._id, chosenEvidence?.match?._id)}
              loading={isLoading.validate}
            >
              Reassign
            </Button>
          </Flex>
        </Stack>
      );
    }
  };

  return (
    <Modal
      yOffset={150}
      opened={verifyOpened}
      onClose={() => setVerifyOpened(false)}
      h="40vw"
      size={multiSelect ? "40vw" : "32vw"}
    >
      {renderModalContent()}
    </Modal>
  );
};

export default AiAssignmentVerificationModal;